import {
  GetDraftConfigResponse,
  GetPaymentsSummaryByTermNumberResponse,
  GetPolicyDraftResponseType,
  GetPolicyResponseTypeV2,
} from '@clearcover/core-app-types';
import { useAuth0 } from 'Auth';
import {
  PolicyDetailsDisplay,
  PolicyDraftHeaderBar,
  StaggeredAnimatedComponent,
  StaggeredAnimationContainer,
} from 'Policies/components';
import { PolicyDetailsHeader } from 'Policies/components/PolicyDetailsHeader';
import { SuccessModal } from 'Policies/components/PolicySummary';
import { QuoteBar } from 'Policies/components/QuoteBar';
import { PolicyContext } from 'Policies/utils';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { LoadingPage } from 'Shared/components';
import { useResourceEffectiveGrants } from 'Shared/hooks';
import { useApi } from 'Shared/services/Api';
import { useLazyApiV2 } from 'Shared/services/Api/makeApiHttp';
import { useVehicleInspectionEligibility } from 'Shared/services/Api/useVehicleInspectionEligibility';
import { combineStrings } from 'Shared/utils';
import styles from './styles.module.css';
import { DriverOptionsResponse } from 'Policies/components/PolicyDriversSection/DriverForm';

export const PolicyDraft: FC<
  RouteComponentProps<
    {
      id: string;
      draftId: string;
    },
    StaticContext,
    {
      updatePolicySuccess?: boolean;
    }
  >
> = ({ match, history, location }) => {
  const { user } = useAuth0();

  const [{ data: policy, loading, error, refetch: executeGetPolicyDraft }] =
    useApi<GetPolicyDraftResponseType>(
      `/policies/${match.params.id}/drafts/${match.params.draftId}`
    );

  const [driverOptions, setDriverOptions] =
    useState<DriverOptionsResponse | null>(null);

  const [draftList, setDraftList] = useState<
    GetPolicyResponseTypeV2['draftList']
  >([]);

  useEffect(() => {
    setDraftList(policy?.policyDraft.draftList ?? []);
  }, [policy?.policyDraft.draftList]);

  const [
    { loading: billingLoading, data: billingInfoResponse, error: billingError },
    fetchBillingData,
  ] = useLazyApiV2<GetPaymentsSummaryByTermNumberResponse>({
    url: `/payments/${match.params.id}/summary/${
      ['renewal', 'renewal_quote'].includes(policy?.policyDraft?.draftType)
        ? 'renewal'
        : ''
    }`,
  });
  // Wait for policy to load before fetching billing data
  useEffect(() => {
    if (policy) {
      fetchBillingData();
    }
  }, [policy, fetchBillingData]);
  const [
    { data: draftConfig, loading: draftConfigLoading, error: draftconfigError },
  ] = useApi<GetDraftConfigResponse>('/draft/config');

  const { loading: resourceEffectiveGrantsLoading, resourceEffectiveGrants } =
    useResourceEffectiveGrants(match.params.id);

  const {
    data: vehicleInspectionEligibility,
    refetch: fetchVehicleInspectionEligibility,
  } = useVehicleInspectionEligibility({ policyId: match.params.id });

  const refetchHandler = useCallback(() => {
    executeGetPolicyDraft();
    fetchVehicleInspectionEligibility();
  }, [executeGetPolicyDraft, fetchVehicleInspectionEligibility]);

  if (loading || resourceEffectiveGrantsLoading || draftConfigLoading)
    return <LoadingPage />;
  if (error) return <div>{error.message}</div>;
  if (draftconfigError) return <div>{draftconfigError.message}</div>;
  if (!policy)
    return (
      <h1>No data found for the policy draft by id #{match.params.draftId}</h1>
    );
  const policyDraft = policy.policyDraft;
  const policyDetails = policyDraft.policyDetails;
  const draftType = policyDraft.draftType;
  const applicantName = combineStrings([
    policyDraft.policyHolder.name.firstName,
    policyDraft.policyHolder.name.lastName,
  ]);

  const isRenewalDraft = draftType === 'renewal';

  const isEndorseablePolicy =
    !policyDraft.disableEndorsements &&
    (policyDetails.termInfo.termStatusDetailsList.every((status) =>
      [
        'pending_renewal',
        'non_renewed_by_insured',
        'non_renewed_by_insurer',
        'renewed',
        'future_renewal',
      ].includes(status)
    ) ||
      policyDetails.termInfo.termStatus !== 'expired' ||
      draftType === 'renewal');

  const hasRegionalEvent = policyDraft.regionalEvents.length > 0;

  const canEndorseDuringRegionalEvents =
    !hasRegionalEvent || user?.partner?.name === 'clearcover';

  const hasProcessOnePaymentMethod = policyDraft.billing.paymentMethods.some(
    (method) => method.paymentProcessor === 'PROCESS_ONE'
  );

  const policyId = policyDetails.policyId;
  const renewalDraftId = draftList.find((d) => d.type === 'renewal')?.id;

  return (
    <PolicyContext.Provider
      value={{
        details: {
          region: policyDetails.policyRegion,
          effectiveDate: policyDraft.effectiveDate ?? policyDetails.bindDate,
          timeZone: policyDetails.policyTimeZone,
          filingIdentifier: policyDetails.filingIdentifier,
          configDiscoveryParams: policyDetails.configDiscoveryParams,
          isPolicyDraft: true,
          draftType,
          isRenewalDraft,
          termId: policyDetails.termInfo.termId,
          mailingState: policyDetails.addresses.mailing.state,
          showDriverCfrs: policyDetails.showDriverCfrs,
          askForUserReportedLicenseStatus:
            policyDetails.askForUserReportedLicenseStatus ?? false,
          requiredAccessStatements: policyDraft.requiredAccessStatements,
          showMilitarySection: policyDetails.showMilitarySection ?? true,
          showStudentSection: policyDetails.showStudentSection ?? true,
          showEducation: policyDetails.showEducation ?? true,
          showOccupation: policyDetails.showOccupation ?? true,
          showRegisteredToApplicantOrSpouse:
            policyDetails.showRegisteredToApplicantOrSpouse ?? true,
          showExcludedDriverGender:
            policyDetails.showExcludedDriverGender ?? false,
          showExcludedDriverMaritalStatus:
            policyDetails.showExcludedDriverMaritalStatus ?? false,
          showPricingData: policyDetails.showPricingData ?? true,
        },
        person: {
          name: applicantName,
        },
        flags: {
          renewalQuotesEnabled: draftConfig.renewalQuotesEnabled,
          mandatedDownPay: policyDraft.mandatedDownPay,
          isFailingUnderwriting: policyDraft?.isFailingUnderwriting ?? false,
        },
        canEndorseDuringRegionalEvents,
        isEndorseablePolicy,
        systemOfRecord: 'clearcover',
        resourceEffectiveGrants,
        refetchHandler: refetchHandler,
        loading,
        productSegment: policyDetails.productSegment || 'standardAuto',
        draftList: draftList,
        removeDraft: (draftId) =>
          setDraftList(draftList.filter((draft) => draft.id !== draftId)),
        setDriverOptions,
        driverOptions,
      }}
    >
      <StaggeredAnimationContainer className={styles.container}>
        <PolicyDraftHeaderBar
          policyId={policyId}
          renewalDraftId={renewalDraftId}
          draftType={draftType}
          updatedAt={policyDraft.updatedAt}
          timeZone={policyDetails.policyTimeZone}
        />
        {location.state?.updatePolicySuccess && (
          <SuccessModal
            changeType={draftType === 'renewal' ? 'renewal' : 'draft'}
          />
        )}

        <StaggeredAnimatedComponent>
          <PolicyDetailsHeader
            applicantName={applicantName}
            effectivePeriod={policyDetails.effectivePeriod}
            policyNumber={policyDetails.policyNumber}
            termInfo={policyDetails.termInfo}
            termStatusDetailsList={policyDetails.termInfo.termStatusDetailsList}
            draftList={
              policyDraft.draftType === 'renewal'
                ? policyDraft.draftList.filter(
                    (draft) => draft.type === 'renewal_quote'
                  )
                : []
            }
            renewalDraftId={renewalDraftId}
            processOne={hasProcessOnePaymentMethod}
          />
        </StaggeredAnimatedComponent>

        <PolicyDetailsDisplay
          policy={policyDraft}
          billingInfo={{
            billingInfoResponse,
            loading: billingLoading,
            error: billingError,
            fetchBillingData,
          }}
          vehicleInspectionEligibility={vehicleInspectionEligibility}
        />
        <QuoteBar
          showQuoteBar={!isRenewalDraft}
          billingChangeSummary={policy.billingChangeSummary}
          onSaveDraft={() => {
            if (draftType === 'renewal_quote') {
              // Note: Although `renewalDraftId` is nullable, it will always have a value
              // for renewal quotes, as they are built off of an existing renewal drafts.
              // If the renewal draft is terminated or applied, the renewal quote will be
              // discarded automatically.
              history.push(`/policies/${policyId}/draft/${renewalDraftId}`);
            } else {
              history.push(`/policies/${policyId}`, {});
            }
          }}
          onUpdatePolicy={() => {
            history.push(
              `/policies/${match.params.id}/draft/${match.params.draftId}/summary`,
              location.state
            );
          }}
          renewalDraftId={renewalDraftId}
        />
      </StaggeredAnimationContainer>
    </PolicyContext.Provider>
  );
};
